import {
  Avatar,
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { avatarBg, getAvatarUrl, getInitials } from "~/utils/avatar";
import { trpc } from "~/utils/trpc";
import { CenterModal } from "../CenterModal";
import {
  MODAL_FORM_LAYOUT,
  modalFormFieldLabelSx,
  modalFormIntroBodySx,
  modalFormMultilineTextFieldSx,
  modalFormSelectSx,
  modalFormTextFieldSx,
} from "../modalFormStyles";
import { useToast } from "../ToastProvider";

export interface EditProjectModalProject {
  id: string;
  teamId: string;
  name: string;
  description: string | null;
  assigneeId: string;
  messengerChannelName: string | null;
  messengerChannelId: string | null;
}

interface EditProjectModalProps {
  open: boolean;
  onClose: () => void;
  project: EditProjectModalProject | null;
}

export default function EditProjectModal({
  open,
  onClose,
  project,
}: EditProjectModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showActionToast } = useToast();
  const utils = trpc.useUtils();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [assigneeError, setAssigneeError] = useState("");

  // Re-sync local form state whenever the target project changes (different
  // project picked from the sidebar) or the modal re-opens.
  useEffect(() => {
    if (!open || !project) return;
    setName(project.name ?? "");
    setNameError("");
    setDescription(project.description ?? "");
    setAssigneeId(project.assigneeId ?? "");
    setAssigneeError("");
  }, [open, project]);

  const { data: membersData } = trpc.team.members.useQuery(
    { teamId: project?.teamId ?? "" },
    { enabled: open && !!project },
  );
  const members = membersData?.members;

  const updateMutation = trpc.project.update.useMutation({
    onSuccess: (updated) => {
      showActionToast({ title: t("project.updateSuccess"), description: t("project.updateSuccessDesc", { name: updated.name }), severity: "success" });
      utils.project.list.invalidate();
      onClose();
    },
    onError: () => showActionToast({ title: t("project.updateFailed"), severity: "error" }),
  });

  const handleSubmit = () => {
    if (!project) return;
    if (!name.trim()) {
      setNameError(t("project.nameRequired"));
      return;
    }
    if (!assigneeId) {
      setAssigneeError(t("project.assigneeRequired"));
      return;
    }
    setNameError("");
    setAssigneeError("");
    updateMutation.mutate({
      projectId: project.id,
      name: name.trim(),
      description: description.trim() || undefined,
      assigneeId,
    });
  };

  const labelSx = modalFormFieldLabelSx(theme);
  const inputSx = modalFormTextFieldSx(theme);
  const multilineSx = modalFormMultilineTextFieldSx(theme);
  const selectSx = modalFormSelectSx(theme);

  const channelLabel =
    project?.messengerChannelName
      ? `# ${project.messengerChannelName}`
      : project?.messengerChannelId ?? "—";

  const isPending = updateMutation.isPending;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const originalDescription = (project?.description ?? "").trim();
  const hasChanges =
    trimmedName !== (project?.name ?? "") ||
    trimmedDescription !== originalDescription ||
    assigneeId !== (project?.assigneeId ?? "");

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("project.editTitle")}
      size="md"
      width={480}
      hideCloseButton
    >
      <Box>
        <Stack spacing={MODAL_FORM_LAYOUT.fieldStackGap}>
          <Typography variant="body1" sx={modalFormIntroBodySx(theme)}>
            {t("project.channelDescription")}
          </Typography>

          {/* Project name */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.name")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
              sx={inputSx}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.description")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              sx={multilineSx}
            />
          </Box>

          {/* Assignee */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.assignee")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={assigneeId}
              onChange={(e) => {
                setAssigneeId(e.target.value);
                setAssigneeError("");
              }}
              error={Boolean(assigneeError)}
              aria-label={t("project.assignee")}
              sx={selectSx}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.custom.gray[500] }}
                    >
                      {t("project.assigneeRequired")}
                    </Typography>
                  );
                }
                const m = members?.find((m) => m.id === selected);
                const name = m?.messengerNickname ?? m?.user?.name ?? "";
                return (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={m?.user ? getAvatarUrl(m.user) : undefined}
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: 9,
                        fontWeight: 700,
                        bgcolor: avatarBg(name),
                      }}
                    >
                      {getInitials(name)}
                    </Avatar>
                    <Typography variant="body2">{name}</Typography>
                  </Stack>
                );
              }}
            >
              {members?.map((m) => {
                const name = m.messengerNickname ?? m.user?.name ?? "";
                return (
                  <MenuItem key={m.id} value={m.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        src={m.user ? getAvatarUrl(m.user) : undefined}
                        sx={{
                          width: 20,
                          height: 20,
                          fontSize: 9,
                          fontWeight: 700,
                          bgcolor: avatarBg(name),
                        }}
                      >
                        {getInitials(name)}
                      </Avatar>
                      <Typography variant="body2">{name}</Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
            {assigneeError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                {assigneeError}
              </Typography>
            )}
          </Box>

          {/* Channel — read-only display */}
          <Box>
            <Typography
              component="label"
              variant="body2"
              sx={{ ...labelSx, color: theme.palette.custom.gray[500] }}
            >
              {t("project.channel")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: 44,
                px: 2,
                py: 0.5,
                bgcolor: theme.palette.custom.gray[100],
                border: `1px solid ${theme.palette.custom.gray[400]}`,
                borderRadius: "6px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.custom.gray[500],
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {channelLabel}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Actions */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{
            mt: MODAL_FORM_LAYOUT.gapFieldSectionToActions,
            pb: MODAL_FORM_LAYOUT.actionsPaddingBottom,
          }}
        >
          <Button
            variant="text"
            onClick={onClose}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: theme.palette.custom.primary,
              px: 2,
              "&:hover": {
                bgcolor: theme.palette.custom.primarySecondaryHover,
              },
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!trimmedName || !assigneeId || !hasChanges || isPending}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "0.5rem",
              bgcolor: theme.palette.custom.primary,
              px: 3,
              "&:hover": { bgcolor: theme.palette.custom.primaryHover },
              "&.Mui-disabled": {
                bgcolor: theme.palette.custom.primaryDisabled,
                color: theme.palette.custom.gray[0],
              },
            }}
          >
            {t("project.saveChanges")}
          </Button>
        </Stack>
      </Box>
    </CenterModal>
  );
}
